/** Zustand persist store — user preferences (separate from main app-store) */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Locale } from "@/lib/i18n";
import { SUPPORTED_LOCALES } from "@/lib/i18n";

function pickInitialLocale(raw: string): Locale {
  const normalized = raw.toLowerCase();
  if (normalized.startsWith("zh")) {
    if (/zh-(tw|hk|mo|hant)/i.test(raw)) return "zh-Hant";
    return "zh-Hans";
  }
  const primary = normalized.split("-")[0];
  const hit = (SUPPORTED_LOCALES as readonly string[]).find(
    (l) => l === primary || l === normalized,
  );
  return (hit ?? "en") as Locale;
}

export interface UserPreferences {
  // Appearance
  theme: "light" | "dark" | "system";
  compactMode: boolean;
  // Language
  locale: Locale;
  // Chart
  defaultTimeframe: string;
  chartCandleStyle: "candle" | "bar" | "line";
  defaultIndicators: string[];
  // Trading
  defaultOrderType: "market" | "limit";
  confirmOrders: boolean;
  // Notifications
  priceAlerts: boolean;
  tradeNotifications: boolean;
  newsAlerts: boolean;
  // Experience (Phase 1)
  experienceLevel: "essential" | "advanced" | "professional";
  scoreCardExpanded: boolean;
  aiResultFormat: "scorecard" | "report" | "both";
}

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: "light",
  compactMode: false,
  locale: "en",
  defaultTimeframe: "1D",
  chartCandleStyle: "candle",
  defaultIndicators: ["SMA20", "RSI"],
  defaultOrderType: "market",
  confirmOrders: true,
  priceAlerts: true,
  tradeNotifications: true,
  newsAlerts: false,
  // New users default to essential; existing users get "advanced" via migration
  experienceLevel: "essential",
  scoreCardExpanded: false,
  aiResultFormat: "scorecard",
};

interface PreferencesState {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => void;
  resetPreferences: () => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      preferences: { ...DEFAULT_PREFERENCES },
      updatePreference: (key, value) =>
        set((state) => ({
          preferences: { ...state.preferences, [key]: value },
        })),
      resetPreferences: () =>
        set({ preferences: { ...DEFAULT_PREFERENCES } }),
    }),
    {
      name: "xfincompass-preferences",
      version: 4,
      storage: createJSONStorage(() => localStorage),
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as PreferencesState;
        if (version < 2) {
          const detected =
            typeof navigator !== "undefined"
              ? navigator.language
              : "en";
          const locale = pickInitialLocale(detected ?? "");
          return {
            ...state,
            preferences: {
              ...state.preferences,
              locale: locale as Locale,
            },
          };
        }
        if (version < 3) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const prefs = (state.preferences ?? {}) as any;
          return {
            ...state,
            preferences: {
              ...prefs,
              experienceLevel: prefs.experienceLevel ?? "advanced",
              scoreCardExpanded: prefs.scoreCardExpanded ?? false,
              aiResultFormat: prefs.aiResultFormat ?? "report",
            },
          };
        }
        if (version < 4) {
          // zh split: legacy "zh" → "zh-Hans".
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const prefs = (state.preferences ?? {}) as any;
          if (prefs.locale === "zh") {
            prefs.locale = "zh-Hans";
          }
          return { ...state, preferences: prefs };
        }
        return state;
      },
    },
  ),
);
